import React from 'react';
import type { BarDuration } from '@einhaus/project314-types';
import { trpc } from '@/api/trpc';
import { useAppQuery } from '@/hooks/useAppQuery';
import Tooltip from '@/components/UI/Tooltip';
import {
    buildRuleChips,
    buildTradeLogicV2ExitSummary,
    getRuleKey,
    normalizeDirection,
    type TradeLogicV2ExitDisplayRule,
    type TradeLogicV2ExitDisplaySummary,
    type TradeLogicV2ExitRuleChip,
    type TradeLogicV2ExitSummaryViewModel
} from './tradeLogicV2ExitSummaryModel';

export type { TradeLogicV2ExitDisplayRule, TradeLogicV2ExitDisplaySummary } from './tradeLogicV2ExitSummaryModel';

/** A compiled exit candidate is immutable, so its rules never need refetching once loaded. */
const EXIT_RULES_STALE_TIME_MS = Infinity;
/** Rules beyond this collapse into a "+N more" chip; the rest stay one hover away. */
const MAX_INLINE_RULES = 3;

const RULE_TONES: Record<string, string> = {
    'max hold': 'bg-zinc-800 text-zinc-300',
    'option expiry': 'bg-amber-900/40 text-amber-200',
    'profit taking': 'bg-green-900/40 text-green-200',
    'profit target': 'bg-green-900/40 text-green-200',
    'sell at level': 'bg-sky-900/40 text-sky-200',
    'stop loss': 'bg-red-900/40 text-red-200'
};

const DEFAULT_RULE_TONE = 'bg-zinc-800 text-zinc-300';

interface TradeLogicV2ExitSummaryStrategy {
    tradeLogicV2ExitVersionId?: number | null;
    /** Lets bar-count thresholds be described as elapsed market time. */
    duration?: BarDuration;
}

interface TradeLogicV2ExitSummaryProps {
    strategy?: TradeLogicV2ExitSummaryStrategy | null;
    exitCandidateId?: number | null;
    exitCandidateHash?: string | null;
    /**
     * Pre-built rules from a caller that already loaded them with its own payload. Left unset, the
     * rules are fetched here, which is the path that also resolves bar counts into market time.
     */
    exitCandidateDisplay?: TradeLogicV2ExitDisplaySummary | null;
    /** Position side being described; rules gated to the other side are hidden. */
    direction?: string | null;
    className?: string;
    showDisplay?: boolean;
    showCandidateInfo?: boolean;
}

const TradeLogicV2ExitSummary: React.FC<TradeLogicV2ExitSummaryProps> = ({
    strategy,
    exitCandidateId,
    exitCandidateHash,
    exitCandidateDisplay,
    direction,
    className = '',
    showDisplay = true,
    showCandidateInfo = true
}) => {
    const versionId = strategy?.tradeLogicV2ExitVersionId ?? null;
    const barDuration = strategy?.duration ?? null;
    const shouldFetchRules = !exitCandidateDisplay && exitCandidateId !== null && exitCandidateId !== undefined;

    const versionQuery = useAppQuery({
        ...trpc.strategy.fetchTradeLogicDefinitionVersionById.queryOptions({
            versionId: versionId ?? 0
        }),
        enabled: versionId !== null
    });

    const rulesQuery = useAppQuery({
        ...trpc.strategy.fetchExitCandidateDisplaySummaries.queryOptions({
            candidateIds: [exitCandidateId ?? 0],
            barDuration
        }),
        enabled: shouldFetchRules,
        staleTime: EXIT_RULES_STALE_TIME_MS
    });

    const definition = versionQuery.data?.definition ?? null;
    const version = versionQuery.data?.version ?? null;

    if (versionId === null) return null;

    const fetchedDisplay =
        exitCandidateId !== null && exitCandidateId !== undefined
            ? (rulesQuery.data?.summariesByCandidateId[exitCandidateId] ?? null)
            : null;

    const summary = buildTradeLogicV2ExitSummary({
        definition,
        direction: normalizeDirection(direction),
        exitCandidateDisplay: exitCandidateDisplay ?? fetchedDisplay,
        exitCandidateHash,
        exitCandidateId,
        strategyVersion: version?.version ?? null,
        versionId
    });

    return renderTradeLogicV2ExitSummary({ className, showCandidateInfo, showDisplay, summary });
};

const getRuleTone = (rule: TradeLogicV2ExitDisplayRule | TradeLogicV2ExitRuleChip): string =>
    RULE_TONES[rule.reason ?? ''] ?? DEFAULT_RULE_TONE;

const renderTradeLogicV2ExitSummary = ({
    className,
    showCandidateInfo,
    showDisplay,
    summary
}: {
    className: string;
    showCandidateInfo: boolean;
    showDisplay: boolean;
    summary: TradeLogicV2ExitSummaryViewModel;
}) =>
    showDisplay ? (
        <TradeLogicV2ExitSummaryCard className={className} showCandidateInfo={showCandidateInfo} summary={summary} />
    ) : (
        <TradeLogicV2ExitSummaryInline className={className} showCandidateInfo={showCandidateInfo} summary={summary} />
    );

const RuleChip = ({ tone, children }: { tone: string; children: React.ReactNode }) => (
    <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ${tone}`}>{children}</span>
);

const RuleDetailList = ({ showCandidateInfo, summary }: { showCandidateInfo: boolean; summary: TradeLogicV2ExitSummaryViewModel }) => (
    <div className="space-y-2">
        <div className="font-medium text-zinc-100">This trade exits when any of these happen:</div>

        <ul className="space-y-1.5">
            {summary.rules.map((rule, index) => (
                <li className="border-l border-zinc-700 pl-2" key={getRuleKey(rule, index)}>
                    <div className="text-zinc-100">{rule.headline}</div>
                    <div className="text-zinc-400">
                        {rule.action} when {rule.condition}
                    </div>
                </li>
            ))}
        </ul>

        <div className="text-zinc-500">
            {summary.definitionLabel}
            {showCandidateInfo ? ` · ${summary.candidateLabel}` : ''}
        </div>
    </div>
);

const TradeLogicV2ExitSummaryInline = ({
    className,
    showCandidateInfo,
    summary
}: {
    className: string;
    showCandidateInfo: boolean;
    summary: TradeLogicV2ExitSummaryViewModel;
}) => {
    if (!summary.rules.length) {
        return (
            <div className={`text-sm text-zinc-300 ${className}`}>
                {summary.displayLabel}
                {showCandidateInfo ? <span className="text-zinc-500"> · {summary.candidateLabel}</span> : null}
            </div>
        );
    }

    const chips = buildRuleChips(summary.rules);
    const visibleChips = chips.slice(0, MAX_INLINE_RULES);
    const hiddenChipCount = chips.length - visibleChips.length;

    return (
        <div className={className}>
            <Tooltip content={<RuleDetailList showCandidateInfo={showCandidateInfo} summary={summary} />}>
                {/* Focusable so the rules are reachable by keyboard and by tap, where hover never fires. */}
                <button className="flex cursor-help flex-wrap gap-1 text-left" type="button">
                    {visibleChips.map((chip) => (
                        <RuleChip key={chip.headline} tone={getRuleTone(chip)}>
                            {chip.headline}
                        </RuleChip>
                    ))}
                    {hiddenChipCount > 0 ? <RuleChip tone={DEFAULT_RULE_TONE}>+{hiddenChipCount} more</RuleChip> : null}
                </button>
            </Tooltip>
        </div>
    );
};

const TradeLogicV2ExitSummaryCard = ({
    className,
    showCandidateInfo,
    summary
}: {
    className: string;
    showCandidateInfo: boolean;
    summary: TradeLogicV2ExitSummaryViewModel;
}) => (
    <div className={`mb-3 rounded-lg bg-zinc-900 p-3 ${className}`}>
        <div className="mb-2 text-sm text-zinc-400">{summary.rules.length ? 'Exits when any of these happen:' : 'Exit rules:'}</div>

        {summary.rules.length > 0 ? (
            <ul className="mb-3 space-y-2">
                {summary.rules.map((rule, index) => (
                    <li className="border-l border-zinc-700 pl-3" key={getRuleKey(rule, index)}>
                        <div className="flex flex-wrap items-center gap-2">
                            <RuleChip tone={getRuleTone(rule)}>{rule.headline}</RuleChip>
                            <span className="text-sm font-medium text-zinc-100">{rule.action}</span>
                        </div>
                        <div className="text-sm text-zinc-300">When {rule.condition}</div>
                    </li>
                ))}
            </ul>
        ) : summary.description ? (
            <div className="mb-3 text-sm text-zinc-300">{summary.description}</div>
        ) : (
            <div className="mb-3 text-sm text-zinc-300">{summary.displayLabel}</div>
        )}

        <div className="text-xs text-zinc-500">
            {summary.definitionLabel}
            {showCandidateInfo ? (
                <>
                    {' · '}
                    <span className="break-all font-mono">{summary.candidateLabel}</span>
                </>
            ) : null}
        </div>
    </div>
);

export default TradeLogicV2ExitSummary;
